import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { admin } from '../lib/api/admin.ts'
import type { TenantDirectory } from '../lib/api/admin.ts'
import type { AiDecision, AiFinding, AiFindingDetail, AiFindingEvent } from '../lib/api/aiFindings.ts'
import { describeError } from '../lib/api/messages.ts'
import { useTelemetrySource } from '../lib/api/source.tsx'
import {
  PERIOD_OPTIONS,
  STATUS_OPTIONS,
  VERDICT_OPTIONS,
  canDecide,
  formatConfidence,
  periodFrom,
  verdictPresentation,
} from '../lib/ai/semantics.ts'
import { formatClockShort, formatDayClock } from '../lib/format.ts'
import { navigate, useSearchParams } from '../lib/router.tsx'
import type { LogRow } from '../lib/sse/types.ts'
import { useAction } from '../lib/useAction.ts'
import { useResource } from '../lib/useResource.ts'
import { PageHeader, Toolbar } from '../components/layout/PageHeader.tsx'
import { Button } from '../components/primitives/Button.tsx'
import { ConfirmDialog } from '../components/primitives/Dialog.tsx'
import { SelectField } from '../components/primitives/Field.tsx'
import { EmptyState, ErrorState, LoadingRow } from '../components/primitives/States.tsx'
import { AgentPicker, TenantSelect } from '../components/logs/Directory.tsx'
import { EventDossier } from '../components/logs/EventDossier.tsx'
import { FindingDossier, Kbd } from '../components/ai/FindingDossier.tsx'
import { FindingRadar, RadarLegend } from '../components/ai/Radar.tsx'

const PAGE_SIZE = 100

/** Sem `status` na URL = a fila do analista. `todos` tira o filtro. */
const ALL = 'todos'

interface Filters {
  status: string | undefined
  statusParam: string
  verdict: string
  tenantId: string
  endpointId: string
  period: string
}

function readFilters(search: URLSearchParams, multiTenant: boolean): Filters {
  const statusParam = search.get('status') ?? 'analyzed'
  return {
    statusParam,
    status: statusParam === ALL ? undefined : statusParam,
    verdict: search.get('verdict') ?? '',
    tenantId: multiTenant ? (search.get('tenantId') ?? '') : '',
    endpointId: search.get('endpointId') ?? '',
    period: search.get('periodo') ?? '',
  }
}

function filtersHref(filters: Filters, selected: string | null = null): string {
  const search = new URLSearchParams()
  if (filters.statusParam !== 'analyzed') search.set('status', filters.statusParam)
  if (filters.verdict !== '') search.set('verdict', filters.verdict)
  if (filters.tenantId !== '') search.set('tenantId', filters.tenantId)
  if (filters.endpointId !== '') search.set('endpointId', filters.endpointId)
  if (filters.period !== '') search.set('periodo', filters.period)
  if (selected !== null) search.set('achado', selected)
  const text = search.toString()
  return text === '' ? '/analise-ia' : `/analise-ia?${text}`
}

interface ListState {
  items: AiFinding[]
  nextCursor: string | null
  hasMore: boolean
  loading: boolean
  error: unknown
}

const EMPTY: ListState = { items: [], nextCursor: null, hasMore: false, loading: true, error: null }

/**
 * Análise por IA — a mesa do analista.
 *
 * Em cima, o radar: máquinas × janelas de 15 min do que está carregado, para
 * enxergar campanha em vez de linha solta. À esquerda, a fila; à direita, o
 * dossiê do achado aberto. `J`/`K` andam pela fila; no console, `C`/`D` abrem a
 * confirmação da decisão. Nada é executado na máquina por causa de um veredito.
 */
export function AiFindings() {
  const source = useTelemetrySource()
  const search = useSearchParams()
  const filters = useMemo(() => readFilters(search, source.multiTenant), [search, source.multiTenant])
  const selectedParam = search.get('achado')

  // ── lista por cursor ──────────────────────────────────────────────────────
  const [list, setList] = useState<ListState>(EMPTY)
  const [nonce, setNonce] = useState(0)
  const abortRef = useRef<AbortController | null>(null)

  const fetchPage = useCallback(
    (cursor: string | null, append: boolean) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setList((prev) => ({ ...(append ? prev : EMPTY), loading: true, error: null }))

      source
        .aiFindings(
          {
            tenantId: filters.tenantId || undefined,
            endpointId: filters.endpointId || undefined,
            status: filters.status,
            verdict: filters.verdict || undefined,
            from: periodFrom(filters.period, Date.now()),
            cursor,
            pageSize: PAGE_SIZE,
          },
          controller.signal,
        )
        .then((page) => {
          if (controller.signal.aborted) return
          setList((prev) => ({
            items: append ? [...prev.items, ...page.items] : page.items,
            nextCursor: page.next_cursor,
            hasMore: page.has_more,
            loading: false,
            error: null,
          }))
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return
          setList((prev) => ({ ...prev, loading: false, error }))
        })
    },
    [source, filters],
  )

  useEffect(() => {
    fetchPage(null, false)
    return () => abortRef.current?.abort()
  }, [fetchPage, nonce])

  // ── seleção ───────────────────────────────────────────────────────────────
  const selectedId = selectedParam ?? list.items[0]?.id ?? null
  const select = useCallback(
    (id: string | null) => navigate(filtersHref(filters, id), { replace: true }),
    [filters],
  )

  const fetchDetail = useCallback(
    (signal: AbortSignal) =>
      selectedId === null ? Promise.resolve(null) : source.aiFinding(selectedId, signal),
    [source, selectedId],
  )
  const detailResource = useResource<AiFindingDetail | null>(fetchDetail)
  // A decisão devolve o achado: ele substitui o lido, sem nova busca.
  const [patched, setPatched] = useState<AiFinding | null>(null)
  useEffect(() => setPatched(null), [selectedId])
  const detail =
    detailResource.data !== null && detailResource.data.finding.id === selectedId
      ? patched !== null && patched.id === selectedId
        ? { ...detailResource.data, finding: patched }
        : detailResource.data
      : null

  // ── empresas (só console) ─────────────────────────────────────────────────
  const fetchTenants = useCallback(
    (signal: AbortSignal) => (source.multiTenant ? admin.tenants(signal) : Promise.resolve(null)),
    [source.multiTenant],
  )
  const tenants = useResource<TenantDirectory | null>(fetchTenants).data
  const tenantName = useCallback(
    (id: string) => tenants?.items.find((t) => t.tenant_id === id)?.name ?? null,
    [tenants],
  )

  // ── decisão ───────────────────────────────────────────────────────────────
  const [pending, setPending] = useState<AiDecision | null>(null)
  const decision = useAction()
  const decide = source.decideAiFinding

  const confirmDecision = (): void => {
    if (decide === null || pending === null || detail === null) return
    const finding = detail.finding
    decision.run(async () => {
      const updated = await decide(finding.id, pending)
      setPending(null)
      setPatched(updated)
      // Na fila filtrada por status, o decidido sai e o próximo abre.
      const index = list.items.findIndex((item) => item.id === updated.id)
      const leaves = filters.status !== undefined && filters.status !== updated.status
      const rest = leaves ? list.items.filter((item) => item.id !== updated.id) : list.items.map((item) => (item.id === updated.id ? updated : item))
      setList((prev) => ({ ...prev, items: rest }))
      if (leaves && index !== -1) select(rest[Math.min(index, rest.length - 1)]?.id ?? null)
    })
  }

  // ── teclado ───────────────────────────────────────────────────────────────
  const [openEvent, setOpenEvent] = useState<LogRow | null>(null)
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.ctrlKey || event.metaKey || event.altKey) return
      const target = event.target as HTMLElement | null
      if (target !== null && (target.isContentEditable || /^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName))) return
      if (openEvent !== null || pending !== null) return

      const key = event.key.toLowerCase()
      if (key === 'j' || key === 'k') {
        const index = list.items.findIndex((item) => item.id === selectedId)
        const next = list.items[index + (key === 'j' ? 1 : -1)]
        if (next !== undefined) {
          event.preventDefault()
          select(next.id)
          document.getElementById(`achado-${next.id}`)?.scrollIntoView({ block: 'nearest' })
        }
      } else if ((key === 'c' || key === 'd') && decide !== null && detail !== null && canDecide(detail.finding.status)) {
        event.preventDefault()
        decision.reset()
        setPending(key === 'c' ? 'confirmed' : 'dismissed')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [list.items, selectedId, select, decide, detail, decision, openEvent, pending])

  const openEventRow = (event: AiFindingEvent): void => {
    if (detail === null) return
    const finding = detail.finding
    // Resumo para o cabeçalho da ficha; o resto vem de `GET /logs/{log_id}`.
    setOpenEvent({
      id: event.log_id,
      event_id: null,
      event_time: event.event_time,
      received_at: event.event_time,
      tenant_id: finding.tenant_id,
      tenant_name: tenantName(finding.tenant_id),
      endpoint_id: finding.endpoint_id,
      hostname: finding.host,
      event_type: event.event_type,
      source: null,
      native_id: null,
      record_id: null,
      payload_size: null,
    })
  }

  const setFilters = (patch: Partial<Filters>): void => navigate(filtersHref({ ...filters, ...patch }))
  const shortcut = (label: string, patch: Partial<Filters>) => {
    const active = Object.entries(patch).every(([k, v]) => filters[k as keyof Filters] === v)
    return (
      <button
        key={label}
        type="button"
        aria-pressed={active}
        onClick={() => setFilters({ verdict: '', ...patch })}
        className={`border px-2 py-0.5 text-micro uppercase tracking-label ${
          active ? 'border-accent text-accent' : 'border-line text-fg-muted hover:text-fg'
        }`}
      >
        {label}
      </button>
    )
  }

  return (
    <>
      <PageHeader
        title="Análise por IA"
        actions={
          <span className="hidden items-center gap-1.5 text-micro text-fg-muted md:flex">
            <Kbd>J</Kbd>
            <Kbd>K</Kbd> navegar
            {decide !== null && (
              <>
                <Kbd>C</Kbd> confirmar <Kbd>D</Kbd> descartar
              </>
            )}
          </span>
        }
      />

      <Toolbar>
        <div className="flex items-center gap-1">
          {shortcut('Fila', { statusParam: 'analyzed', status: 'analyzed' })}
          {shortcut('Ataques', { statusParam: ALL, status: undefined, verdict: 'attack' })}
          {shortcut('Confirmados', { statusParam: 'confirmed', status: 'confirmed' })}
          {shortcut('Tudo', { statusParam: ALL, status: undefined })}
        </div>
        <SelectField
          label="Status"
          value={filters.statusParam}
          onValueChange={(value) => setFilters({ statusParam: value })}
          options={[{ value: ALL, label: 'Todos' }, ...STATUS_OPTIONS]}
          className="w-44"
        />
        <SelectField
          label="Veredito"
          value={filters.verdict}
          onValueChange={(value) => setFilters({ verdict: value })}
          options={[{ value: '', label: 'Todos' }, ...VERDICT_OPTIONS]}
          className="w-36"
        />
        <SelectField
          label="Período"
          value={filters.period}
          onValueChange={(value) => setFilters({ period: value })}
          options={PERIOD_OPTIONS}
          className="w-40"
        />
        {source.multiTenant && (
          <TenantSelect value={filters.tenantId} onChange={(value) => setFilters({ tenantId: value })} />
        )}
        {filters.endpointId !== '' ? (
          <Button size="sm" onClick={() => setFilters({ endpointId: '' })}>
            {list.items[0]?.host ?? 'máquina'} ✕
          </Button>
        ) : (
          <div className="ml-auto min-w-0">
            <AgentPicker
              tenantId={filters.tenantId || undefined}
              onPick={(item) => setFilters({ endpointId: item.endpoint_id })}
            />
          </div>
        )}
      </Toolbar>

      {list.error !== null && list.items.length === 0 ? (
        <ErrorState error={list.error} onRetry={() => setNonce((n) => n + 1)} />
      ) : list.loading && list.items.length === 0 ? (
        <LoadingRow label="Carregando achados…" />
      ) : list.items.length === 0 && selectedParam === null ? (
        <EmptyState title="Nenhum achado com estes filtros." />
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 border-b border-line-subtle px-3 pt-2">
            <span className="text-micro uppercase tracking-label text-fg-muted">Radar</span>
            <RadarLegend />
          </div>
          <FindingRadar findings={list.items} selectedId={selectedId} onPick={(f) => select(f.id)} />

          <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
            <nav
              aria-label="Fila de achados"
              className="flex max-h-72 shrink-0 flex-col overflow-y-auto border-b border-line-subtle lg:max-h-none lg:w-80 lg:border-r lg:border-b-0"
            >
              {list.items.map((finding) => (
                <QueueItem
                  key={finding.id}
                  finding={finding}
                  tenant={source.multiTenant ? tenantName(finding.tenant_id) : null}
                  selected={finding.id === selectedId}
                  onSelect={() => select(finding.id)}
                />
              ))}
              {list.error !== null && (
                <p className="px-3 py-2 text-sm text-crit">{describeError(list.error)}</p>
              )}
              {list.hasMore && (
                <Button
                  variant="ghost"
                  className="m-2"
                  busy={list.loading}
                  onClick={() => fetchPage(list.nextCursor, true)}
                >
                  Carregar mais
                </Button>
              )}
            </nav>

            <section className="flex min-h-0 min-w-0 flex-1 flex-col">
              {selectedId === null ? null : detailResource.error !== null && detail === null ? (
                <ErrorState error={detailResource.error} onRetry={detailResource.reload} />
              ) : detail === null ? (
                <LoadingRow label="Carregando o achado…" />
              ) : (
                <FindingDossier
                  detail={detail}
                  tenantName={source.multiTenant ? tenantName(detail.finding.tenant_id) : null}
                  onOpenEvent={openEventRow}
                  onDecide={
                    decide === null
                      ? null
                      : (value) => {
                          decision.reset()
                          setPending(value)
                        }
                  }
                />
              )}
            </section>
          </div>
        </>
      )}

      {openEvent !== null && <EventDossier row={openEvent} onClose={() => setOpenEvent(null)} />}

      {pending !== null && detail !== null && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setPending(null)}
          title={pending === 'confirmed' ? 'Confirmar incidente' : 'Descartar achado'}
          consequence={
            pending === 'confirmed'
              ? `O achado de ${detail.finding.host} passa a incidente confirmado.`
              : `O achado de ${detail.finding.host} é marcado como descartado e sai da fila.`
          }
          confirmLabel={pending === 'confirmed' ? 'Confirmar incidente' : 'Descartar'}
          destructive={pending === 'confirmed'}
          busy={decision.busy}
          error={decision.error !== null ? describeError(decision.error) : null}
          onConfirm={confirmDecision}
        />
      )}
    </>
  )
}

function QueueItem({
  finding,
  tenant,
  selected,
  onSelect,
}: {
  finding: AiFinding
  tenant: string | null
  selected: boolean
  onSelect: () => void
}) {
  const verdict = verdictPresentation(finding.verdict)
  const confidence = formatConfidence(finding.confidence)
  return (
    <button
      id={`achado-${finding.id}`}
      type="button"
      aria-current={selected || undefined}
      onClick={onSelect}
      className={`flex w-full items-start gap-2 border-b border-l-2 border-b-line-subtle px-3 py-2 text-left ${
        verdict?.border ?? 'border-l-transparent'
      } ${selected ? 'bg-raised' : 'hover:bg-inset'} ${finding.status === 'dismissed' ? 'opacity-60' : ''}`}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline gap-2">
          <span className="truncate font-mono text-sm text-fg">{finding.host}</span>
          <span className="ml-auto shrink-0 tnum font-mono text-micro text-fg-muted">
            {formatDayClock(finding.window_start)}–{formatClockShort(finding.window_end)}
          </span>
        </span>
        <span className="line-clamp-2 text-sm text-fg-secondary">
          {finding.hypothesis ?? (verdict === null ? statusLine(finding) : '')}
        </span>
        {tenant !== null && <span className="truncate text-micro text-fg-muted">{tenant}</span>}
      </span>
      {confidence !== null && (
        <span className={`shrink-0 tnum font-mono text-sm ${verdict?.text ?? 'text-fg-muted'}`}>{confidence}</span>
      )}
    </button>
  )
}

function statusLine(finding: AiFinding): string {
  switch (finding.status) {
    case 'triage_only':
      return `Triagem: pontuação ${finding.triage_score}, não foi para a IA`
    case 'awaiting_analysis':
      return 'Aguardando IA'
    case 'analysis_failed':
      return 'Falha na análise'
    default:
      return ''
  }
}
